// Utility functions for state persistence and restoration

const STORAGE_KEY = 'globalState';
const PERSISTENT_KEYS = [
  'currentPage',
  'search',
  'searchText',
  'sortValue',
  'sortType',
  'isFilterUsing',
  'selectedItems',
  'selectedRowKeys',
  'visibleFilter',
  'visibleSort',
  'isArchiveView',
  'archiveMode',
  'selectedOption',
  'selectedCategory',
  'selectedType',
  'date',
  'selectedDate',
  'customFilters'
];

// Save state to localStorage
export const saveStateToStorage = (state) => {
  try {
    const stateToSave = {
      pages: {}
    };

    // Only save persistent keys for each page
    Object.keys(state.pages).forEach(pageName => {
      stateToSave.pages[pageName] = {};
      PERSISTENT_KEYS.forEach(key => {
        if (state.pages[pageName][key] !== undefined) {
          stateToSave.pages[pageName][key] = state.pages[pageName][key];
        }
      });
      stateToSave.pages[pageName]._timestamp = Date.now();
    });

    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  } catch (error) {
    console.error('Error saving state to localStorage:', error);
  }
};

// Load state from localStorage
export const loadStateFromStorage = () => {
  try {
    const serializedState = localStorage.getItem(STORAGE_KEY);
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (error) {
    console.error('Error loading state from localStorage:', error);
    return undefined;
  }
};

// Clear state from localStorage
export const clearStateFromStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing state from localStorage:', error);
  }
};

// Save specific page state
export const savePageStateToStorage = (pageName, pageState) => {
  try {
    const existingState = loadStateFromStorage() || { pages: {} };
    existingState.pages[pageName] = {};
    
    PERSISTENT_KEYS.forEach(key => {
      if (pageState[key] !== undefined) {
        existingState.pages[pageName][key] = pageState[key];
      }
    });
    existingState.pages[pageName]._timestamp = Date.now();

    localStorage.setItem(STORAGE_KEY, JSON.stringify(existingState));
  } catch (error) {
    console.error('Error saving page state to localStorage:', error);
  }
};

export const isPageStateExpired = (pageName, maxAgeMs = 60000) => {
  try {
    const state = loadStateFromStorage();
    if (state && state.pages && state.pages[pageName]) {
      const timestamp = state.pages[pageName]._timestamp;
      if (!timestamp) return true;
      return (Date.now() - timestamp) > maxAgeMs;
    }
    return true;
  } catch {
    return true;
  }
};

// Load specific page state
export const loadPageStateFromStorage = (pageName) => {
  try {
    const state = loadStateFromStorage();
    if (state && state.pages && state.pages[pageName]) {
      return state.pages[pageName];
    }
    return undefined;
  } catch (error) {
    console.error('Error loading page state from localStorage:', error);
    return undefined;
  }
};

// Merge saved state with current state
export const mergeStateWithStorage = (currentState, savedState) => {
  if (!savedState || !savedState.pages) {
    return currentState;
  }

  const mergedState = JSON.parse(JSON.stringify(currentState)); // Deep clone
  
  // Merge global state if needed
  if (savedState.global) {
    mergedState.globalState = {
      ...mergedState.globalState,
      ...savedState.global
    };
  }
  
  // Merge page states
  Object.keys(savedState.pages).forEach(pageName => {
    if (mergedState.globalState && mergedState.globalState.pages && mergedState.globalState.pages[pageName]) {
      mergedState.globalState.pages[pageName] = {
        ...mergedState.globalState.pages[pageName],
        ...savedState.pages[pageName]
      };
      console.log(`Merged state for ${pageName}:`, {
        selectedItems: savedState.pages[pageName]?.selectedItems?.length || 0,
        searchText: savedState.pages[pageName]?.searchText,
        currentPage: savedState.pages[pageName]?.currentPage
      });
    }
  });

  return mergedState;
};

// Check if state has changed and needs to be saved
export const hasStateChanged = (currentState, previousState) => {
  if (!previousState) return true;
  
  const currentPersistent = extractPersistentState(currentState);
  const previousPersistent = extractPersistentState(previousState);
  
  return JSON.stringify(currentPersistent) !== JSON.stringify(previousPersistent);
};

// Extract only persistent keys from state
export const extractPersistentState = (state) => {
  if (!state || !state.pages) return {};
  
  const persistentState = { pages: {} };
  
  Object.keys(state.pages).forEach(pageName => {
    persistentState.pages[pageName] = {};
    PERSISTENT_KEYS.forEach(key => {
      if (state.pages[pageName][key] !== undefined) {
        persistentState.pages[pageName][key] = state.pages[pageName][key];
      }
    });
  });
  
  return persistentState;
};

// Debounced save function
export const createDebouncedSave = (delay = 1000) => {
  let timeoutId;
  
  return (state) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      saveStateToStorage(state);
    }, delay);
  };
};

// State migration utilities
export const migrateState = (savedState, currentVersion = '1.0.0') => {
  // Add migration logic here when needed
  // This is useful for handling state structure changes between versions
  return savedState;
};

// Export state for backup
export const exportState = (state) => {
  const stateToExport = extractPersistentState(state);
  const dataStr = JSON.stringify(stateToExport, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `globalState_${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Import state from backup
export const importState = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedState = JSON.parse(e.target.result);
        resolve(importedState);
      } catch (error) {
        reject(new Error('Invalid state file format'));
      }
    };
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsText(file);
  });
};
